import React, { useState } from "react";
import DSBox from "../../../system-design/DSBox";
import DSCard from "../../../system-design/DSCard";
import DSCardContent from "../../../system-design/DSCardContent";
import DSButton from "../../../system-design/DSButton";
import DSErrorButton from "../../../system-design/DSErrorButton";
import { DSBaseTable, DSTableCell } from "../../../system-design/Table";
import { Column } from "../../../system-design/Table/DSBaseTable/components/DSBaseTable";
import useGetKlydosByIds from "../../hooks/useGetKlydosByIds";
import { Klydo, hasSound } from "../../../../api/klydo";
import KlydoModal from "../../../Klydos/components/KlydoModal";
import DSDialog from "../../../system-design/DSDialog";
import KlydoLoopUrl from "../../../../components/klydoPreview/KlydoLoopUrl";
import DSSectionHeading from "../../../system-design/DSSectionHeading";
import { sectionCardSx } from "../../../system-design/sectionCardSx";
import KlydosPickDialog from "../../../Gifts/GiftCategories/components/KlydosPickDialog";
import ConfirmationModal from "../../../ConfirmationModal";
import { useDevice } from "../../context/DeviceModalContext";
import firebaseService from "../../../../firebase_service/firebaseService";
import { extractApiErrorMessage } from "../../../../utils/util";

type HiddenKlydoRow = Klydo & { klydoId: string; deletedAt: number };

const DeviceHiddenKlydosCard = () => {
  const { device, refetch } = useDevice();
  const [selectedKlydo, setSelectedKlydo] = useState<{ id: string } | null>(
    null,
  );
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showUnhideAllConfirmation, setShowUnhideAllConfirmation] =
    useState(false);
  const [deletingKlydoId, setDeletingKlydoId] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [updateError, setUpdateError] = useState<string | null>(null);

  const hiddenList = device.hiddenKlydos?.value ?? [];
  const ids = hiddenList.map((h) => h.id);
  const { klydos, isLoading, error } = useGetKlydosByIds(ids);

  const deletedAtMap = new Map(hiddenList.map((h) => [h.id, h.deletedAt]));

  const rows: HiddenKlydoRow[] = klydos.map((klydo) => ({
    ...klydo,
    klydoId: klydo.id,
    deletedAt: deletedAtMap.get(klydo.id) ?? 0,
  }));

  const columns: Column<HiddenKlydoRow>[] = [
    { field: "loopUrl", label: "Klydo", sortable: false },
    { field: "name", label: "Name", sortable: false },
    { field: "chimesUrls", label: "Chime", sortable: false },
    { field: "capabilitiesV2", label: "Sound", sortable: false },
    { field: "deletedAt", label: "Deleted At", sortable: false },
  ];

  const handleKlydoClick = (klydo: HiddenKlydoRow) => {
    if (klydo.klydoId) {
      setSelectedKlydo({ id: klydo.klydoId });
    }
  };

  // Every write goes through the read-modify-write helper rather than
  // `updateConfig("hiddenKlydos", ...)`: the clock writes this same array
  // itself whenever the user deletes a klydo on the device, so a whole-array
  // write off the rendered snapshot would un-hide whatever it hid meanwhile.
  const applyHiddenKlydosDiff = async (diff: {
    add?: string[];
    remove?: string[];
  }) => {
    setUpdateError(null);
    try {
      await firebaseService.updateDeviceHiddenKlydos(device.id, diff);
      refetch();
      return true;
    } catch (e) {
      setUpdateError(
        extractApiErrorMessage(e, "Failed to update hidden klydos"),
      );
      return false;
    }
  };

  const handleUnhideKlydo = async (klydo: HiddenKlydoRow) => {
    setDeletingKlydoId(klydo.klydoId);
    try {
      await applyHiddenKlydosDiff({ remove: [klydo.klydoId] });
    } finally {
      setDeletingKlydoId(null);
    }
  };

  const handleEditHiddenKlydos = async (pickedIds: string[]) => {
    const picked = new Set(pickedIds);
    const current = new Set(ids);
    const add = pickedIds.filter((id) => !current.has(id));
    const remove = ids.filter((id) => !picked.has(id));
    if (!add.length && !remove.length) {
      setShowEditDialog(false);
      return;
    }
    setIsSaving(true);
    try {
      if (await applyHiddenKlydosDiff({ add, remove })) {
        setShowEditDialog(false);
      }
    } finally {
      setIsSaving(false);
    }
  };

  const handleUnhideAll = async () => {
    setIsSaving(true);
    try {
      // Removes exactly the ids on screen, not a blanket reset to [], so a
      // klydo the user hid on the clock since this render stays hidden.
      if (await applyHiddenKlydosDiff({ remove: ids })) {
        setShowUnhideAllConfirmation(false);
      }
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <DSBox sx={{ mb: 2 }}>
      <DSCard sx={sectionCardSx}>
        <DSSectionHeading>
          {`Hidden Klydos ${hiddenList.length > 0 ? `(${hiddenList.length})` : ""}`}
        </DSSectionHeading>

        <DSCardContent>
          {error ? (
            <DSBox
              sx={{
                textAlign: "center",
                padding: "20px",
                color: "error.main",
              }}
            >
              Failed to load hidden klydos
            </DSBox>
          ) : isLoading ? (
            <DSBox sx={{ textAlign: "center", padding: "20px" }}>
              Loading hidden klydos...
            </DSBox>
          ) : hiddenList.length > 0 ? (
            <DSBaseTable
              data={rows}
              columns={columns}
              rowBuilder={(klydo) => (
                <>
                  <DSTableCell
                    key="klydo"
                    onClick={() => handleKlydoClick(klydo)}
                    sx={{ cursor: "pointer" }}
                  >
                    <DSBox sx={{ width: 35, height: 35, overflow: "hidden" }}>
                      <KlydoLoopUrl loopUrl={klydo?.loopUrl || ""} />
                    </DSBox>
                  </DSTableCell>
                  <DSTableCell key="name">
                    {klydo?.name || klydo?.creator || "Unknown"}
                  </DSTableCell>
                  <DSTableCell key="chime">
                    {klydo?.chimesUrls?.length ? "✓" : "✗"}
                  </DSTableCell>
                  <DSTableCell key="sound">
                    {hasSound(klydo?.capabilitiesV2?.animationSound?.mode)
                      ? "✓"
                      : "✗"}
                  </DSTableCell>
                  <DSTableCell key="deletedAt">
                    {klydo.deletedAt
                      ? new Date(klydo.deletedAt).toLocaleDateString(
                          "default",
                          {
                            month: "short",
                            day: "numeric",
                            year: "numeric",
                            hour: "2-digit",
                            minute: "2-digit",
                          },
                        )
                      : "-"}
                  </DSTableCell>
                </>
              )}
              onPressDelete={handleUnhideKlydo}
              defaultRowsPerPage={5}
              deletingRowId={deletingKlydoId}
              getRowId={(klydo) => klydo.klydoId}
            />
          ) : (
            <DSBox sx={{ textAlign: "center", padding: "20px" }}>
              No hidden klydos
            </DSBox>
          )}

          {updateError && (
            <DSBox sx={{ color: "error.main", marginTop: "10px" }}>
              {updateError}
            </DSBox>
          )}

          <DSBox sx={{ display: "flex", gap: "10px", marginTop: "15px" }}>
            <DSButton
              disabled={isSaving}
              onClick={() => {
                setUpdateError(null);
                setShowEditDialog(true);
              }}
            >
              Edit Hidden Klydos
            </DSButton>
            {hiddenList.length > 0 && (
              <DSErrorButton
                disabled={isSaving}
                onClick={() => {
                  setUpdateError(null);
                  setShowUnhideAllConfirmation(true);
                }}
                sx={{ minWidth: "120px" }}
              >
                Unhide All
              </DSErrorButton>
            )}
          </DSBox>

          {showEditDialog && (
            <KlydosPickDialog
              title="Hidden Klydos"
              onClose={() => setShowEditDialog(false)}
              selectedKlydos={ids}
              onPickKlydos={handleEditHiddenKlydos}
            />
          )}

          <ConfirmationModal
            open={showUnhideAllConfirmation}
            message={`Are you sure you want to unhide all ${hiddenList.length} hidden klydo(s) for this device? They will become available on the clock again.`}
            onClose={() => setShowUnhideAllConfirmation(false)}
            onConfirm={handleUnhideAll}
          />

          {selectedKlydo && (
            <DSDialog
              fullWidth
              maxWidth="lg"
              open={!!selectedKlydo}
              onClose={() => setSelectedKlydo(null)}
            >
              <KlydoModal
                klydoId={selectedKlydo.id}
                setShowModal={() => setSelectedKlydo(null)}
              />
            </DSDialog>
          )}
        </DSCardContent>
      </DSCard>
    </DSBox>
  );
};

export default DeviceHiddenKlydosCard;
